import {
  PayloadAction,
  createAction,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "..";
import { GameLaunchStatus } from "@onmo/eon-streaming";
import {
  exitPointerLock,
  getLoadingGameMessage,
  getNetworkConditionMessage,
  getPcGameMessage,
} from "../../models/gameplay";
import {
  LoadingStatuses,
  PCGameActionRequiredStatuses,
  PCInGameBlockingStatuses,
} from "../../constants/gamePlay";
import { NetworkConditionStatus, NetworkQuality } from "../../types/network";

export type IStreamSnackbarMessage = {
  type?: string;
  title?: string;
  description?: string;
  icon?: string;
  status?: GameLaunchStatus | NetworkConditionStatus;
  videoURL?: string;
};

interface IGamePlayState {
  streamStatus: GameLaunchStatus;
  loadingStatus?: LoadingStatuses;
  isNetworkDisconnected?: boolean;
  isSessionLost?: boolean;
  networkQuality: NetworkQuality;
  networkConditionStatus?: NetworkConditionStatus;
  activeNetworkCondition?: NetworkConditionStatus;
  gameRunning?: boolean;
  isMenuOpen: boolean;
  isQuitPopupOpen: boolean;
  currentSnackbarMessage?: IStreamSnackbarMessage;
  isSnackbarVisible: boolean;
  keyboardRemoteHint?: boolean;
}

const initialState = {
  streamStatus: GameLaunchStatus.NONE,
  networkQuality: NetworkQuality.Good,
  gameRunning: false,
  isMenuOpen: false,
  isQuitPopupOpen: false,
  currentSnackbarMessage: undefined,
  isSnackbarVisible: false,
} as IGamePlayState;

export const resetGamePlayState = createAction("gamePlay/resetState");

export const gamePlaySlice = createSlice({
  name: "gamePlay",
  initialState: initialState,
  reducers: {
    setStreamStatus: (
      state,
      action: PayloadAction<{ streamStatus: GameLaunchStatus }>,
    ) => {
      state.streamStatus = action.payload.streamStatus;
      if (action.payload.streamStatus === GameLaunchStatus.STATUS_GAMERUNNING) {
        state.gameRunning = true;
      }
    },
    setLoadingStatus: (
      state,
      action: PayloadAction<{
        loadingStatus: LoadingStatuses;
      }>,
    ) => {
      state.loadingStatus = action.payload.loadingStatus;
    },
    setNetworkQuality: (
      state,
      action: PayloadAction<{ networkQuality: NetworkQuality }>,
    ) => {
      state.networkQuality = action.payload.networkQuality;
    },
    setNetworkConditionStatus: (
      state,
      action: PayloadAction<{
        networkConditionStatus?: NetworkConditionStatus;
      }>,
    ) => {
      state.networkConditionStatus = action.payload.networkConditionStatus;
    },
    setActiveNetworkCondition: (
      state,
      action: PayloadAction<{
        activeNetworkCondition?: NetworkConditionStatus;
      }>,
    ) => {
      state.activeNetworkCondition = action.payload.activeNetworkCondition;
    },
    setNetworkDisconnected: (
      state,
      action: PayloadAction<{ isNetworkDisconnected: boolean }>,
    ) => {
      state.isNetworkDisconnected = action.payload.isNetworkDisconnected;
    },
    setSessionLost: (
      state,
      action: PayloadAction<{ isSessionLost: boolean }>,
    ) => {
      state.isSessionLost = action.payload.isSessionLost;
    },
    setMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.isMenuOpen = action.payload;
    },
    setQuitPopupOpen: (state, action: PayloadAction<boolean>) => {
      state.isQuitPopupOpen = action.payload;
    },
    setCurrentSnackbarMessage: (
      state,
      action: PayloadAction<IStreamSnackbarMessage | undefined>,
    ) => {
      state.currentSnackbarMessage = action.payload;
    },
    setSnackbarVisible: (state, action: PayloadAction<boolean>) => {
      state.isSnackbarVisible = action.payload;
    },
    setKeyboardRemoteHint: (state, action: PayloadAction<boolean>) => {
      state.keyboardRemoteHint = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetGamePlayState, () => initialState);
  },
});

// Action creators are generated for each case reducer function
export const {
  setStreamStatus,
  setLoadingStatus,
  setNetworkQuality,
  setNetworkConditionStatus,
  setActiveNetworkCondition,
  setNetworkDisconnected,
  setSessionLost,
  setMenuOpen,
  setQuitPopupOpen,
  setCurrentSnackbarMessage,
  setSnackbarVisible,
  setKeyboardRemoteHint,
} = gamePlaySlice.actions;

export const selectStreamMessage = createSelector(
  [(state: RootState) => state.gamePlay],
  (gamePlay) => {
    const {
      streamStatus,
      isNetworkDisconnected,
      networkConditionStatus,
    } = gamePlay;
    // Network disconnected: show immediately, but don't override an error popup
    if (
      isNetworkDisconnected &&
      !PCInGameBlockingStatuses.includes(streamStatus)
    ) {
      return getPcGameMessage(GameLaunchStatus.NETWORK_DISCONNECTED);
    }

    // Always show error state immediately
    if (streamStatus === GameLaunchStatus.ERROR) {
      return getPcGameMessage(GameLaunchStatus.ERROR);
    }

    // Only show in-game blocking status when stream is running
    if (PCInGameBlockingStatuses.includes(streamStatus)) {
      exitPointerLock();
      return getPcGameMessage(streamStatus);
    }

    // Only show action-required status when stream has finished loading
    if (PCGameActionRequiredStatuses.includes(streamStatus)) {
      return getPcGameMessage(streamStatus);
    }

    // Network unstable (but still connected): only show during gameplay, driven
    // by the active network condition status.
    if (
      streamStatus === GameLaunchStatus.STATUS_GAMERUNNING &&
      networkConditionStatus
    ) {
      return getNetworkConditionMessage(networkConditionStatus);
    }

    return getPcGameMessage(streamStatus);
  },
);

export const selectLoadingMessage = createSelector(
  [
    (state: RootState) => state.gamePlay,
    (_state: RootState, showLaunching?: boolean) => showLaunching,
  ],
  (gamePlay, showLaunching) => {
    const { streamStatus, loadingStatus, isNetworkDisconnected } = gamePlay;

    // Show nothing if stream error
    if (
      isNetworkDisconnected ||
      streamStatus === GameLaunchStatus.ERROR ||
      PCInGameBlockingStatuses.includes(streamStatus)
    )
      return;

    return getLoadingGameMessage(loadingStatus, showLaunching);
  },
);

export const selectIsShowCountry = createSelector(
  [(state: RootState) => state.gamePlay],
  (gamePlay) => {
    const { gameRunning, loadingStatus } = gamePlay;
    return loadingStatus === "Done" && !gameRunning;
  },
);

export default gamePlaySlice.reducer;
